// Firebase provider — party-existence probe.
//
// Relocated (S2) from src/lib/partyValidator.js, verbatim except the Firestore
// query now uses the lazy `getDb()` instead of a module-level `db`.
//
// Used by the Identity boot sequence to decide whether a remembered partyCode
// is still alive or should be treated as expired.
//
// "Expired" is best-effort without a server: we query for a single message in
// the room. Zero docs => party has either never existed or has aged out under
// Firestore's TTL, both of which we want to surface the same way to the user
// (drop them back into GLOB with a one-shot "expired" banner).
import {
  collection,
  query,
  where,
  limit,
  getDocs,
} from '@firebase/firestore';
import { getDb } from './client.js';

export const DEFAULT_PARTY = 'GLOB';

/**
 * Returns `true` when the party is reachable (or is GLOB which is always
 * implicit), `false` when the party appears expired / unknown.
 *
 * Network errors resolve to `true` — we never want a transient outage to
 * unilaterally evict the user from their party. F6.2's offline banner is
 * the right surface for those failures.
 *
 * @param {string | null | undefined} code
 * @returns {Promise<boolean>}
 */
export async function validatePartyExists(code) {
  if (!code) return true;
  if (code === DEFAULT_PARTY) return true;
  try {
    const q = query(
      collection(getDb(), 'message'),
      where('code', '==', code),
      limit(1),
    );
    const snap = await getDocs(q);
    return !snap.empty;
  } catch {
    // Network blip / SDK error → be conservative and keep the user where
    // they were. F6.2 will own the visual feedback.
    return true;
  }
}

export default validatePartyExists;
